"use client";
import { twMerge } from "tailwind-merge";
import { getEntityIcon } from "../../config/entities";
import type { DeviceType } from "../../types/DeviceTypes";
import { Icon, type IconName } from "../Icon";

export interface DeviceTypeIconProps {
  /** The device type to display (deprecated: use iconName instead) */
  deviceType?: DeviceType;
  /** Explicit icon name to display (preferred over deviceType) */
  iconName?: IconName;
  /** Size of the icon (preset or custom pixel size) */
  size?: number | "sm" | "md" | "lg" | "xl";
  /** Show label below the icon */
  showLabel?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Icon name mapping for each device type
 */
const deviceTypeIconMap: Record<DeviceType, IconName> = {
  battery: "BatteryHigh",
  charger: "ChargingStation",
  thermostat: "ThermometerSimple",
  inverter: "Power",
  vehicle: "Car",
  water_heater: "DropHalfBottom",
  meter: "Gauge",
  sensor: getEntityIcon("sensor"),
  gateway: "WifiHigh",
  other: "Cube",
  unknown: "Question",
};

/**
 * Human-readable labels for device types
 */
const deviceTypeLabels: Record<DeviceType, string> = {
  battery: "Battery",
  charger: "Charger",
  thermostat: "Thermostat",
  inverter: "Inverter",
  vehicle: "Vehicle",
  water_heater: "Water Heater",
  meter: "Meter",
  sensor: "Sensor",
  gateway: "Gateway",
  other: "Other",
  unknown: "Unknown",
};

/**
 * DeviceTypeIcon
 *
 * Displays a consistent icon for device types across the platform.
 *
 * **Preferred usage:** Pass `iconName` explicitly (from device-config package)
 * **Legacy usage:** Pass `deviceType` to use built-in icon mapping (deprecated)
 *
 * @example
 * ```tsx
 * // Preferred: Explicit icon from device-config
 * import { getDeviceTypeDisplay } from "@texturehq/device-config";
 * const display = getDeviceTypeDisplay("battery");
 * <DeviceTypeIcon iconName={display.icon} size="md" />
 *
 * // Legacy: Built-in mapping (deprecated)
 * <DeviceTypeIcon deviceType="battery" size="md" />
 * ```
 */
export function DeviceTypeIcon({
  deviceType,
  iconName,
  size = "md",
  showLabel = false,
  className,
}: DeviceTypeIconProps) {
  // Prefer explicit iconName, fallback to deviceType mapping
  const resolvedIconName = iconName || (deviceType ? deviceTypeIconMap[deviceType] : "Question");
  const label = deviceType ? deviceTypeLabels[deviceType] : undefined;

  return (
    <div className={twMerge("inline-flex items-center", showLabel ? "flex-col gap-1" : "gap-0", className)}>
      <Icon name={resolvedIconName} size={size} />
      {showLabel && label && <span className="text-xs text-text-secondary">{label}</span>}
    </div>
  );
}
